import { SHORTCUT_YIELD, useRegisterShortcut, } from '#pplx/frontend/apps/ask/keyboard-shortcuts-provider/registry.js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { defineMessage, useIntl } from 'react-intl';
const STOP_RESPONSE_MESSAGE = defineMessage({
    defaultMessage: 'Stop response (Esc)',
    description: 'Aria label for the button to stop AI response generation',
    id: 'aa7a838f06',
});
export function useStopGeneratingAction({ onStop, isShortcutEnabled, resetKey, }) {
    const intl = useIntl();
    const [isStopInFlight, setIsStopInFlight] = useState(false);
    const onStopRef = useRef(onStop);
    const isStopInFlightRef = useRef(false);
    const stopRequestGenerationRef = useRef(0);
    const previousResetKeyRef = useRef(resetKey);
    onStopRef.current = onStop;
    useEffect(() => {
        if (previousResetKeyRef.current === resetKey)
            return;
        previousResetKeyRef.current = resetKey;
        stopRequestGenerationRef.current += 1;
        isStopInFlightRef.current = false;
        setIsStopInFlight(false);
    }, [resetKey]);
    const handleStop = useCallback(() => {
        const stop = onStopRef.current;
        if (stop === undefined || isStopInFlightRef.current)
            return;
        isStopInFlightRef.current = true;
        setIsStopInFlight(true);
        const requestGeneration = stopRequestGenerationRef.current;
        const finishStop = () => {
            // A prior entry's late completion must not unlock the current entry.
            if (stopRequestGenerationRef.current !== requestGeneration)
                return;
            isStopInFlightRef.current = false;
            setIsStopInFlight(false);
        };
        try {
            void Promise.resolve(stop()).finally(finishStop);
        }
        catch (error) {
            finishStop();
            throw error;
        }
    }, []);
    useStopGeneratingShortcut({
        isEnabled: isShortcutEnabled,
        onStop: handleStop,
    });
    return {
        handleStop,
        isStopInFlight,
        stopLabel: intl.formatMessage(STOP_RESPONSE_MESSAGE),
    };
}
/**
 * Registers the Escape shortcut separately from the visible button so it keeps
 * working when Computer replaces the button with follow-up submission.
 */
export function useStopGeneratingShortcut({ onStop, isEnabled, }) {
    const onStopRef = useRef(onStop);
    onStopRef.current = onStop;
    const handler = useCallback((event) => {
        // If a nested widget already consumed this Escape outside the registry
        // (e.g. a popover or dropdown), yield so we don't turn it into a
        // second "stop generating" action.
        if (event.defaultPrevented)
            return SHORTCUT_YIELD;
        // Mark Escape as handled so later document/window listeners do not also treat it
        // as an unhandled cancel/close keypress after we stop generation.
        event.preventDefault();
        // Intentionally fire-and-forget so the shortcut path does not block on the async handler.
        void onStopRef.current();
    }, []);
    useRegisterShortcut({
        id: 'stop-generating',
        handler,
        isEnabled,
    });
}
//# sourceMappingURL=StopButtonShared.js.map